/**
 * Pure backend-sanitizer for an email campaign's target selection, extracted
 * from EmailCampaignForm (where it was a `useCallback(…, [])` — already pure).
 *
 * `contact` and `ai_prospect` recipients are emailed by their raw address
 * (crm-api maps recipients straight to `email`, with no customer lookup), so
 * they are downgraded to `'manual'` before saving. AI prospects in particular
 * are emailed WITHOUT touching the CRM — the email is the identity, nothing is
 * written back. The original id is kept as the manual recipient's unique key.
 * Every other recipient type passes through unchanged.
 *
 * `filters.relationship` / `filters.exclude` (see `audience-builder/facets.ts`)
 * pass through untouched, but an empty `relationship: []` or an empty
 * `exclude: []` are pruned to keep payloads minimal and to stay
 * byte-for-byte back-compat with legacy `industries`-only filters.
 *
 * No money / reseller here — wall-neutral.
 */
import type { TargetSelection } from "./email-target-selector"
import type { AudienceFilters } from "./audience-builder/facets"

/** Drop an empty `relationship` array and an empty `exclude` array so the
 * saved payload only ever carries facets that actually narrow the audience. */
function sanitizeFilters(filters: AudienceFilters | undefined): AudienceFilters | undefined {
  if (!filters) return undefined

  const { relationship, industries, exclude } = filters
  return {
    ...(relationship && relationship.length > 0 ? { relationship } : {}),
    ...(industries ? { industries } : {}),
    ...(exclude && exclude.length > 0 ? { exclude } : {}),
  }
}

/** Downgrade `contact` / `ai_prospect` recipients to `manual` for the backend. */
export function sanitizeTargetSelection(targetSelection: TargetSelection): TargetSelection {
  const filters = sanitizeFilters(targetSelection.filters)
  return {
    ...targetSelection,
    recipients: targetSelection.recipients.map((recipient) => {
      if (recipient.type === "contact" || recipient.type === "ai_prospect") {
        return {
          ...recipient,
          type: "manual" as const,
          // Keep the original id as the unique key for the manual recipient.
          id: recipient.id,
        }
      }
      return recipient
    }),
    ...(filters ? { filters } : {}),
  }
}
